
import { defineComponent } from "vue";
import * as fb from "../../../firebase";
import { random3Digits } from "@/mixins/tools";

type Side = "left" | "right" | "self";
type Operation = "add" | "remove";
type PointType = "binary" | "rank";

interface LegBalance {
  pending: number;
  paid: number;
  lost: number;
}
interface BinaryBalances {
  placed: boolean;
  left: LegBalance;
  right: LegBalance;
  matched: number;
  // The user's OWN (inner) leg; the opposite leg is spillover. null when unknown
  // (unplaced, or the node carries no side). It is a SIDE, not innerLegUnlocked.
  innerSide: "left" | "right" | null;
}
interface DispersalReport {
  nodesVisited: number;
  nodesRemoved: number;
  removed: number;
  notRecoverable: number;
  perNode: Array<{
    username: string;
    leg: string;
    removed: number;
    notRecoverable: number;
  }>;
}

export default defineComponent({
  name: "adjust-points",
  props: {
    username: { type: String, required: true },
  },
  emits: ["pointsAdjusted", "error"],
  data() {
    return {
      enabled: false,
      adjusting: false,
      loadingBalances: false,
      armed: false,
      idempotencyKey: "",
      // Record-date inputs, minted with idempotencyKey and resent unchanged on
      // retry so the backend builds a DETERMINISTIC record doc id (see review()).
      atMs: 0,
      rand: "",
      pointType: "binary" as PointType,
      side: "left" as Side,
      operation: "add" as Operation,
      // OPTIONAL propagate-up flag (default OFF). The chosen leg is ALWAYS
      // credited; ON also rolls the SAME amount up to every upline.
      propagate: false,
      amount: null as number | null,
      reason: "",
      balances: null as BinaryBalances | null,
      resultReport: null as DispersalReport | null,
    };
  },
  computed: {
    // HELD features (wired but disabled): RANK (any) and REMOVE ("quitar").
    // Saturday ships binary ADD only. These block `review()` even if a control
    // is somehow set, matching the server-side guards.
    rankHeld(): boolean {
      return this.pointType === "rank";
    },
    removeHeld(): boolean {
      return this.operation === "remove";
    },
    featureHeld(): boolean {
      return this.rankHeld || this.removeHeld;
    },
    validAmount(): boolean {
      return (
        this.amount !== null && Number.isInteger(this.amount) && this.amount > 0
      );
    },
    canReview(): boolean {
      if (!this.enabled || this.featureHeld) return false;
      if (!this.validAmount || this.reason.trim() === "") return false;
      if (this.side !== "left" && this.side !== "right") return false;
      if (this.loadingBalances) return false;
      return true;
    },
    matchPreview(): string {
      if (!this.balances || !this.validAmount) return "";
      if (this.side !== "left" && this.side !== "right") return "";
      const amount = this.amount as number;
      const other = this.side === "left" ? "right" : "left";
      const currentMatched = this.balances.matched;
      // The chosen leg is ALWAYS credited, so the match math is the leg-credit
      // one. `propagate` only ADDS an upline note; it never changes THIS user's
      // own match.
      const newSide = this.balances[this.side].pending + amount;
      const newMatched = Math.min(newSide, this.balances[other].pending);
      const delta = newMatched - currentMatched;
      const legMsg =
        delta > 0
          ? `Agregar ${amount} pts a la pierna ${this.side} disparará un MATCH de ${delta} pts que el job de pago pagará.`
          : `Agregar ${amount} pts a la pierna ${this.side} NO dispara match (la pierna ${other} sigue siendo la débil).`;
      return this.propagate
        ? `${legMsg} Además, propaga el mismo volumen hacia arriba a los uplines.`
        : legMsg;
    },
  },
  watch: {
    username() {
      this.resetForm();
      this.fetchBalances();
    },
  },
  mounted() {
    // Balances load as soon as a user is shown — NOT gated by the enable switch.
    // Cost: one `Tools-GetUserPointsBalances` callable = 1 node doc + 2 summary
    // sub-docs. The switch only arms the ADD form.
    if (this.username) this.fetchBalances();
  },
  methods: {
    // Role of a leg for the badge. The backend `innerSide` field always carries
    // the node's binarySide — which is the user's DERRAME / powerline leg: where
    // UPLINE spillover lands (people the user did NOT personally enroll). The
    // OPPOSITE leg is the "interna": the only leg the user personally enrolls
    // into. So the field-matching side = "derrame", the opposite = "interna".
    // "" = unknown (hide). Driven by innerSide → shows at 0|0.
    legRole(side: "left" | "right"): "interna" | "derrame" | "" {
      const inner = this.balances?.innerSide ?? null;
      if (inner === null) return "";
      return inner === side ? "derrame" : "interna";
    },
    fetchBalances() {
      if (!this.username) return;
      this.loadingBalances = true;
      const getBalances = fb.func.httpsCallable("Tools-GetUserPointsBalances");
      getBalances({ username: this.username })
        .then((response) => {
          const resp = response.data;
          if (resp.OPCODE === "SUCCESS") {
            this.balances = resp.data.binary as BinaryBalances;
          } else {
            this.$emit(
              "error",
              "Error al obtener saldos de puntos",
              resp.message + "\n" + (resp.messageDetails || "")
            );
          }
        })
        .catch((error) => {
          this.$emit(
            "error",
            "Error al obtener saldos de puntos",
            error.message
          );
        })
        .finally(() => {
          this.loadingBalances = false;
        });
    },
    review() {
      if (!this.canReview) return;
      // Mint the idempotency key ONCE for this confirmation, so a double click on
      // "Confirmar" reuses the SAME key and the backend rejects the duplicate.
      this.idempotencyKey =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `${this.username}-${Date.now()}-${Math.random()}`;
      // Mint the record's date-key inputs ONCE too (client clock = record date).
      // Resent unchanged on a Confirmar double-click, so the backend-built record
      // id (`generateDateKey(new Date(atMs), true, rand)@manual-<op>`) is stable.
      this.atMs = Date.now();
      this.rand = random3Digits();
      this.resultReport = null;
      this.armed = true;
    },
    cancel() {
      this.armed = false;
      this.idempotencyKey = "";
      this.atMs = 0;
      this.rand = "";
    },
    submit() {
      if (this.adjusting) return;
      this.adjusting = true;
      const adjust = fb.func.httpsCallable("Tools-AdjustUserPoints");
      adjust({
        username: this.username,
        pointType: this.pointType,
        side: this.side,
        amount: this.amount,
        operation: this.operation,
        propagate: this.propagate,
        reason: this.reason,
        idempotencyKey: this.idempotencyKey,
        atMs: this.atMs,
        rand: this.rand,
      })
        .then((response) => {
          const resp = response.data;
          if (resp.OPCODE === "SUCCESS") {
            this.$emit("pointsAdjusted", resp.data);
            this.resultReport =
              (resp.data && (resp.data.report as DispersalReport)) || null;
            this.armed = false;
            this.idempotencyKey = "";
            this.atMs = 0;
            this.rand = "";
            this.amount = null;
            this.reason = "";
            this.fetchBalances();
          } else {
            this.$emit(
              "error",
              "Error al ajustar puntos",
              resp.message + "\n" + (resp.messageDetails || "")
            );
          }
        })
        .catch((error) => {
          this.$emit("error", "Error al ajustar puntos", error.message);
        })
        .finally(() => {
          this.adjusting = false;
        });
    },
    resetForm() {
      this.armed = false;
      this.idempotencyKey = "";
      this.atMs = 0;
      this.rand = "";
      this.amount = null;
      this.reason = "";
      this.balances = null;
      this.resultReport = null;
    },
  },
});
